import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { fetchCourseById, updateCourse, deleteCourse } from "../pages/services/courseService";
import { slugify, slugError, WEBSITE_COURSE_URL } from "./utils/courseSlug";
import "../styles/AddForm.css";
import "../styles/UpdateBatch.css";

const Field = ({ label, required, error, children, hint }) => (
  <div className="af-field">
    <label className="af-label">
      {label}{required && <span className="af-required">*</span>}
      {hint && <span style={{ fontSize: 11, color: "#9ca3af", marginLeft: 6, fontWeight: 400 }}>{hint}</span>}
    </label>
    {children}
    {error && <span className="af-error">{error}</span>}
  </div>
);

const UpdateCourse = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    courseTitle: "",
    courseSlug: "",
    courseDescription: "",
    courseImg: "",
    courseDuration: "",
    courseFee: "",
    curriculumDescription: "",
    courseFeatures: [],
    courseCurriculum: [],
    Tools: [],
    faq: [],
  });

  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [errors, setErrors] = useState({});
  // The slug as it was stored when the form opened. Changing an existing slug
  // moves the course's web address, which breaks every link already shared, so
  // the form warns when it differs.
  const [originalSlug, setOriginalSlug] = useState("");

  useEffect(() => {
    fetchCourseById(id)
      .then((data) => {
        setFormData({
          courseTitle: data.courseTitle || "",
          courseSlug: data.courseSlug || "",
          courseDescription: data.courseDescription || "",
          courseImg: data.courseImg || "",
          courseDuration: data.courseDuration || "",
          courseFee: data.courseFee ?? "",
          curriculumDescription: data.curriculumDescription || "",
          courseFeatures: data.courseFeatures || [],
          courseCurriculum: data.courseCurriculum || [],
          Tools: data.Tools || [],
          faq: data.faq || [],
        });
        setOriginalSlug(data.courseSlug || "");
      })
      .catch(() => setErrorMessage("Failed to load course data."))
      .finally(() => setFetchLoading(false));
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
    setErrors((p) => ({ ...p, [name]: "" }));
  };

  const handleSlugChange = (e) => {
    setFormData((p) => ({ ...p, courseSlug: e.target.value.toLowerCase() }));
    setErrors((p) => ({ ...p, courseSlug: "" }));
  };

  // Offered only when a course has no slug. Deliberately a button, not an
  // automatic fill: an existing course's address is the admin's decision.
  const generateSlugFromTitle = () => {
    setFormData((p) => ({ ...p, courseSlug: slugify(p.courseTitle) }));
    setErrors((p) => ({ ...p, courseSlug: "" }));
  };

  const validate = () => {
    const e = {};
    if (!formData.courseTitle.trim()) e.courseTitle = "Course title is required";
    const slugMsg = slugError(formData.courseSlug);
    if (slugMsg) e.courseSlug = slugMsg;
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const ve = validate();
    if (Object.keys(ve).length > 0) { setErrors(ve); setErrorMessage("Please correct the errors below."); return; }
    setLoading(true);
    setErrorMessage("");
    try {
      const payload = {
        ...formData,
        courseSlug: formData.courseSlug.trim(),
        courseFee: formData.courseFee === "" ? null : Number(formData.courseFee),
      };
      await updateCourse(id, payload);
      navigate(`/courses/${id}`, { state: { successMessage: "Course updated successfully!" } });
    } catch (err) {
      if (err.fields && typeof err.fields === "object") setErrors((p) => ({ ...p, ...err.fields }));
      setErrorMessage(err.message || "Update failed.");
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await deleteCourse(id);
      navigate("/courses", { state: { successMessage: "Course deleted successfully!" } });
    } catch (err) {
      setErrorMessage(err.message || "Failed to delete course.");
      setShowDeletePopup(false);
    }
  };

  // ── Feature helpers ──────────────────────────────────────────
  const addFeature = () => setFormData((p) => ({ ...p, courseFeatures: [...p.courseFeatures, { featureIcon: "", featureTitle: "", featureDescription: "" }] }));
  const removeFeature = (i) => setFormData((p) => ({ ...p, courseFeatures: p.courseFeatures.filter((_, idx) => idx !== i) }));
  const updateFeature = (i, field, val) => setFormData((p) => {
    const arr = [...p.courseFeatures]; arr[i] = { ...arr[i], [field]: val }; return { ...p, courseFeatures: arr };
  });

  // ── Tool helpers ─────────────────────────────────────────────
  const addTool = () => setFormData((p) => ({ ...p, Tools: [...p.Tools, { toolImg: "", toolsName: "" }] }));
  const removeTool = (i) => setFormData((p) => ({ ...p, Tools: p.Tools.filter((_, idx) => idx !== i) }));
  const updateTool = (i, field, val) => setFormData((p) => {
    const arr = [...p.Tools]; arr[i] = { ...arr[i], [field]: val }; return { ...p, Tools: arr };
  });

  // ── FAQ helpers ──────────────────────────────────────────────
  const addFaq = () => setFormData((p) => ({ ...p, faq: [...p.faq, { faqQuestion: "", faqAnswer: "" }] }));
  const removeFaq = (i) => setFormData((p) => ({ ...p, faq: p.faq.filter((_, idx) => idx !== i) }));
  const updateFaq = (i, field, val) => setFormData((p) => {
    const arr = [...p.faq]; arr[i] = { ...arr[i], [field]: val }; return { ...p, faq: arr };
  });

  // ── Curriculum helpers ───────────────────────────────────────
  const addModule = () => setFormData((p) => ({ ...p, courseCurriculum: [...p.courseCurriculum, { moduleTitle: "", moduleDuration: "", topics: [] }] }));
  const removeModule = (mi) => setFormData((p) => ({ ...p, courseCurriculum: p.courseCurriculum.filter((_, i) => i !== mi) }));
  const updateModule = (mi, field, val) => setFormData((p) => {
    const arr = [...p.courseCurriculum]; arr[mi] = { ...arr[mi], [field]: val }; return { ...p, courseCurriculum: arr };
  });
  const addTopic = (mi) => setFormData((p) => {
    const arr = [...p.courseCurriculum];
    arr[mi] = { ...arr[mi], topics: [...(arr[mi].topics || []), { title: "", description: "" }] };
    return { ...p, courseCurriculum: arr };
  });
  const removeTopic = (mi, ti) => setFormData((p) => {
    const arr = [...p.courseCurriculum];
    arr[mi] = { ...arr[mi], topics: arr[mi].topics.filter((_, i) => i !== ti) };
    return { ...p, courseCurriculum: arr };
  });
  const updateTopic = (mi, ti, field, val) => setFormData((p) => {
    const arr = [...p.courseCurriculum];
    const topics = [...arr[mi].topics]; topics[ti] = { ...topics[ti], [field]: val };
    arr[mi] = { ...arr[mi], topics }; return { ...p, courseCurriculum: arr };
  });

  if (fetchLoading) {
    return (
      <div className="af-page">
        <div className="af-card" style={{ maxWidth: 780, margin: "0 auto", padding: 40, textAlign: "center" }}>
          <div className="af-spinner" style={{ margin: "0 auto 12px" }} />
          <p style={{ color: "#6b7280" }}>Loading course data…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="af-page">
      {loading && (
        <div className="af-overlay">
          <div className="af-spinner" />
          <p className="af-overlay-text">Saving changes…</p>
        </div>
      )}

      {showDeletePopup && (
        <div className="uf-delete-overlay">
          <div className="uf-delete-modal">
            <p className="uf-delete-msg">Are you sure you want to delete <strong>{formData.courseTitle}</strong>? This action cannot be undone.</p>
            <div className="uf-delete-actions">
              <button className="uf-cancel-btn" onClick={() => setShowDeletePopup(false)}>Cancel</button>
              <button className="uf-confirm-btn" onClick={confirmDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}

      <div className="af-card">
        <div className="af-header">
          <div className="af-header-icon indigo">📘</div>
          <div className="af-header-text" style={{ flex: 1 }}>
            <h2 className="af-title">Update Course</h2>
            <p className="af-subtitle">Edit course details, curriculum, and more</p>
          </div>
          <button type="button" className="uf-delete-trigger" onClick={() => setShowDeletePopup(true)}>Delete</button>
        </div>

        {errorMessage && <div className="af-alert af-alert-error" style={{ margin: "16px 32px 0" }}>{errorMessage}</div>}

        <form onSubmit={handleSubmit}>
          <div className="af-body">

            {/* Basic Info */}
            <div className="af-section">
              <p className="af-section-title">Basic Information</p>
              <Field label="Course Title" required error={errors.courseTitle}>
                <input type="text" name="courseTitle" placeholder="e.g. Full Stack Web Development"
                  value={formData.courseTitle} onChange={handleChange}
                  className={errors.courseTitle ? "error-input" : ""} />
              </Field>
              <Field label="Course Slug" required error={errors.courseSlug} hint="the course's page address on the website">
                <div style={{ display: "flex", gap: 8 }}>
                  <input type="text" name="courseSlug" placeholder="e.g. ai-ml-placement-training"
                    value={formData.courseSlug} onChange={handleSlugChange}
                    autoCapitalize="none" autoCorrect="off" spellCheck={false}
                    style={{ flex: 1 }}
                    className={errors.courseSlug ? "error-input" : ""} />
                  {!originalSlug && (
                    <button type="button" className="af-secondary-btn" onClick={generateSlugFromTitle}
                      disabled={!formData.courseTitle.trim()} style={{ whiteSpace: "nowrap" }}>
                      Generate from title
                    </button>
                  )}
                </div>
                <span style={{ display: "block", fontSize: 12, color: "#6b7280", marginTop: 6, wordBreak: "break-all" }}>
                  Website address: <strong style={{ color: "#374151" }}>{WEBSITE_COURSE_URL}{formData.courseSlug || "…"}</strong>
                </span>
                {!originalSlug && (
                  <span style={{ display: "block", fontSize: 12, color: "#b45309", marginTop: 6 }}>
                    This course has no slug yet, so its page on the website cannot open — clicking it leads to /courses/undefined.
                  </span>
                )}
                {originalSlug && formData.courseSlug.trim() !== originalSlug && (
                  <span style={{ display: "block", fontSize: 12, color: "#b45309", marginTop: 6 }}>
                    ⚠ Changing the slug moves this course to a new address. Links already shared — ads, WhatsApp messages,
                    bookmarks to <strong>{WEBSITE_COURSE_URL}{originalSlug}</strong> — will stop working.
                  </span>
                )}
              </Field>
              <Field label="Description">
                <textarea name="courseDescription" rows={4} placeholder="Describe what this course covers…"
                  value={formData.courseDescription} onChange={handleChange} />
              </Field>
              <div className="af-row">
                <Field label="Duration" hint="e.g. 3 months">
                  <input type="text" name="courseDuration" placeholder="e.g. 6 months"
                    value={formData.courseDuration} onChange={handleChange} />
                </Field>
                <Field label="Fee (₹)">
                  <input type="number" name="courseFee" placeholder="e.g. 25000" min={0}
                    value={formData.courseFee} onChange={handleChange} />
                </Field>
              </div>
              <Field label="Course Image URL" hint="Direct URL to the course thumbnail">
                <input type="text" name="courseImg" placeholder="https://example.com/course-image.jpg"
                  value={formData.courseImg} onChange={handleChange} />
              </Field>
            </div>

            {/* Course Features */}
            <div className="af-section">
              <p className="af-section-title">Course Features</p>
              {formData.courseFeatures.map((f, i) => (
                <div key={i} style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "14px 16px", marginBottom: 10 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
                    <span style={{ fontWeight: 600, fontSize: 13, color: "#374151" }}>Feature {i + 1}</span>
                    <button type="button" onClick={() => removeFeature(i)} style={{ background: "none", border: "none", color: "#ef4444", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>Remove</button>
                  </div>
                  <div className="af-row">
                    <Field label="Icon (emoji or URL)">
                      <input type="text" placeholder="e.g. 🎓 or icon URL" value={f.featureIcon}
                        onChange={(e) => updateFeature(i, "featureIcon", e.target.value)} />
                    </Field>
                    <Field label="Title">
                      <input type="text" placeholder="e.g. Live Sessions" value={f.featureTitle}
                        onChange={(e) => updateFeature(i, "featureTitle", e.target.value)} />
                    </Field>
                  </div>
                  <Field label="Description">
                    <textarea rows={2} placeholder="Brief description of this feature…" value={f.featureDescription}
                      onChange={(e) => updateFeature(i, "featureDescription", e.target.value)} />
                  </Field>
                </div>
              ))}
              <button type="button" className="af-secondary-btn" onClick={addFeature}>+ Add Feature</button>
            </div>

            {/* Tools */}
            <div className="af-section">
              <p className="af-section-title">Tools & Technologies</p>
              {formData.Tools.map((t, i) => (
                <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-end", marginBottom: 10 }}>
                  <Field label="Tool Name" style={{ flex: 1 }}>
                    <input type="text" placeholder="e.g. React" value={t.toolsName}
                      onChange={(e) => updateTool(i, "toolsName", e.target.value)} />
                  </Field>
                  <Field label="Logo URL" style={{ flex: 2 }}>
                    <input type="text" placeholder="https://example.com/react-logo.png" value={t.toolImg}
                      onChange={(e) => updateTool(i, "toolImg", e.target.value)} />
                  </Field>
                  <button type="button" onClick={() => removeTool(i)}
                    style={{ background: "none", border: "1.5px solid #ef4444", color: "#ef4444", borderRadius: 6, padding: "6px 12px", cursor: "pointer", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
                    ✕
                  </button>
                </div>
              ))}
              <button type="button" className="af-secondary-btn" onClick={addTool}>+ Add Tool</button>
            </div>

            {/* Curriculum */}
            <div className="af-section">
              <p className="af-section-title">Curriculum</p>
              <Field label="Curriculum Overview">
                <textarea name="curriculumDescription" rows={3} placeholder="Brief overview of the course curriculum…"
                  value={formData.curriculumDescription} onChange={handleChange} />
              </Field>
              {formData.courseCurriculum.map((mod, mi) => (
                <div key={mi} style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "14px 16px", marginBottom: 12 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
                    <span style={{ fontWeight: 700, fontSize: 13, color: "#374151" }}>Module {mi + 1}</span>
                    <button type="button" onClick={() => removeModule(mi)} style={{ background: "none", border: "none", color: "#ef4444", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>Remove Module</button>
                  </div>
                  <div className="af-row">
                    <Field label="Module Title">
                      <input type="text" placeholder="e.g. Introduction to React" value={mod.moduleTitle}
                        onChange={(e) => updateModule(mi, "moduleTitle", e.target.value)} />
                    </Field>
                    <Field label="Duration">
                      <input type="text" placeholder="e.g. 1 week" value={mod.moduleDuration}
                        onChange={(e) => updateModule(mi, "moduleDuration", e.target.value)} />
                    </Field>
                  </div>
                  {/* Topics */}
                  <p style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.5px", margin: "10px 0 8px" }}>Topics</p>
                  {(mod.topics || []).map((topic, ti) => (
                    <div key={ti} style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 8 }}>
                      <div style={{ flex: 1 }}>
                        <input type="text" placeholder="Topic title" value={topic.title}
                          onChange={(e) => updateTopic(mi, ti, "title", e.target.value)}
                          style={{ width: "100%", marginBottom: 4 }} />
                        <input type="text" placeholder="Topic description (optional)" value={topic.description}
                          onChange={(e) => updateTopic(mi, ti, "description", e.target.value)}
                          style={{ width: "100%" }} />
                      </div>
                      <button type="button" onClick={() => removeTopic(mi, ti)}
                        style={{ background: "none", border: "1.5px solid #ef4444", color: "#ef4444", borderRadius: 6, padding: "5px 10px", cursor: "pointer", fontSize: 12, marginTop: 2 }}>
                        ✕
                      </button>
                    </div>
                  ))}
                  <button type="button" onClick={() => addTopic(mi)}
                    style={{ background: "none", border: "1.5px dashed #6366f1", color: "#6366f1", borderRadius: 6, padding: "5px 12px", cursor: "pointer", fontSize: 12, fontWeight: 600, marginTop: 4 }}>
                    + Add Topic
                  </button>
                </div>
              ))}
              <button type="button" className="af-secondary-btn" onClick={addModule}>+ Add Module</button>
            </div>

            {/* FAQ */}
            <div className="af-section">
              <p className="af-section-title">FAQ</p>
              {formData.faq.map((item, i) => (
                <div key={i} style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "14px 16px", marginBottom: 10 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
                    <span style={{ fontWeight: 600, fontSize: 13, color: "#374151" }}>FAQ {i + 1}</span>
                    <button type="button" onClick={() => removeFaq(i)} style={{ background: "none", border: "none", color: "#ef4444", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>Remove</button>
                  </div>
                  <Field label="Question">
                    <input type="text" placeholder="e.g. Who is this course for?" value={item.faqQuestion}
                      onChange={(e) => updateFaq(i, "faqQuestion", e.target.value)} />
                  </Field>
                  <Field label="Answer">
                    <textarea rows={3} placeholder="Provide a clear, helpful answer…" value={item.faqAnswer}
                      onChange={(e) => updateFaq(i, "faqAnswer", e.target.value)} />
                  </Field>
                </div>
              ))}
              <button type="button" className="af-secondary-btn" onClick={addFaq}>+ Add FAQ</button>
            </div>
          </div>

          <div className="af-submit-row" style={{ display: "flex", gap: 12 }}>
            <button type="button" className="af-secondary-btn" onClick={() => navigate(`/courses/${id}`)} style={{ flex: "0 0 auto" }}>Cancel</button>
            <button type="submit" className="af-submit" disabled={loading} style={{ flex: 1 }}>
              {loading ? "Saving…" : "Update Course"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UpdateCourse;
